import React from "react";
import { Avatar, Box, Paper, Typography } from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import { alpha, useTheme } from "@mui/material/styles";
import moment from "moment";


export const useOrderDetailStyles = makeStyles((theme) => ({
  root: {
    "& .order-detail-summary": {
      listStyle: "none",
      margin: 0,
      padding: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      overflow: "hidden",

      "& li": {
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 38,
        padding: "6px 12px",
        fontSize: 13,
        wordBreak: "break-word",

        "&.odd": { background: theme.palette.background.default },
        "&.even": { background: theme.palette.background.paper },

        "& label": {
          flex: "0 0 45%",
          maxWidth: "45%",
          fontSize: 12,
          fontWeight: 500,
          color: theme.palette.text.secondary,
        },

        // inputs / selects / date pickers fill the value column
        "& > .MuiTextField-root, & > .MuiBox-root, & > div": {
          flex: 1,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
        },
      },

      [theme.breakpoints.down("sm")]: {
        "& li": {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 4,
          padding: "8px 12px",
          "& label": { flex: "none", maxWidth: "100%" },
        },
      },
    },

    "& .ag-cell.custom-style": {
      backgroundColor: theme.palette.secondary.main,
      borderColor: theme.palette.error.main,
    },

    "& .w-150": {
      width: 150,
      [theme.breakpoints.down("sm")]: { width: "100%" },
    },

    "& .MuiAccordionSummary-content": { alignItems: "center" },

    // grids scroll sideways on small screens instead of squashing columns
    "& .order-grid": {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      [theme.breakpoints.down("md")]: {
        "& > div": { minWidth: 640 },
      },
    },

    // preview modal (images, pdf, video, word)
    "& .modal .modal-content": {
      maxWidth: "95vw",
      maxHeight: "85vh",
      [theme.breakpoints.down("sm")]: { width: "95vw" },
    },
  },
}));



export const tabsProps = {
  variant: "scrollable",
  scrollButtons: "auto",
  allowScrollButtonsMobile: true,
};

export const tabSx = {
  alignItems: "start",
  minHeight: 44,
  textTransform: "none",
  fontWeight: 500,
  color: "text.secondary",
  "&.Mui-selected": { color: "text.primary", fontWeight: 700 },
};



export const ToolbarField = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      width: { xs: "100%", sm: "auto" },
      minWidth: { sm: 170 },
    }}
  >
    <Typography
      component="label"
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

export const OrderToolbar = ({ title, subtitle, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", lg: "flex-end" },
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ wordBreak: "break-word" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-end" },
          justifyContent: { sm: "flex-end" },
          gap: 1.5,
        }}
      >
        {children}
      </Box>
    </Box>
  </Paper>
);


export const SectionHeader = ({ title, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      pt: 2,
      pb: 1,
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {children && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {children}
      </Box>
    )}
  </Box>
);



export const CommentItem = ({ isOwn, initial, name, source, time, text }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isOwn ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 1,
        my: 1.5,
      }}
    >
      <Avatar
        sx={{
          bgcolor: deepOrange[500],
          width: 32,
          height: 32,
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {initial}
      </Avatar>

      <Box
        sx={{
          maxWidth: { xs: "85%", md: "70%" },
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: isOwn
            ? alpha(theme.palette.secondary.main, 0.15)
            : theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            columnGap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {name}
            {source ? ` (${source})` : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {time && moment(time).format("DD-MM-YYYY hh:mm a")}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
};